import { createElement } from 'react'
import { Link } from 'react-router-dom'
import type { ReactNode } from 'react'

// Story #3477 Stream 7 (#3474): regex matching patent IDs in rationale prose.
// Covers CN, WO, US, EP, AU, JP and similar two-letter country prefix formats.
const PATENT_ID_RE = /\b([A-Z]{2}\d{6,12}[A-Z]?\d*(?:-[A-Z]\d+)?)\b/g

/**
 * Scan `text` for patent identifiers and wrap each match in a React Router
 * Link pointing to /patents/{encodedId}. Returns a ReactNode (array of
 * strings and Link elements).
 */
export function linkPatentIds(text: string): ReactNode {
  if (!text) return text
  const parts: ReactNode[] = []
  let last = 0
  let match: RegExpExecArray | null
  PATENT_ID_RE.lastIndex = 0
  while ((match = PATENT_ID_RE.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index))
    const id = match[1]
    parts.push(
      createElement(Link, {
        key: `${id}-${match.index}`,
        to: `/patents/${encodeURIComponent(id)}`,
        className: 'text-iflow-600 underline hover:text-iflow-800',
        onClick: (e: React.MouseEvent) => e.stopPropagation(),
      }, id)
    )
    last = match.index + match[0].length
  }
  if (last < text.length) parts.push(text.slice(last))
  return parts.length === 1 ? parts[0] : parts
}

/**
 * #4612: URL to the full patent record on Google Patents (claims, full text,
 * drawings). Prefer the URL the corpus stores; otherwise build it from the
 * patent id — Google Patents uses the hyphen-free form (US-2025062434-A1 ->
 * US20250624 34A1 -> US20250062434A1).
 */
export function googlePatentsUrl(patent: { google_patents_url?: string; patent_id?: string }): string | null {
  if (patent?.google_patents_url) return patent.google_patents_url
  const id = (patent?.patent_id || '').replace(/-/g, '').toUpperCase()
  return id ? `https://patents.google.com/patent/${id}` : null
}
